import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Map, CheckCircle2, KeyRound, Sparkles, Ban, Rocket, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_META = [
  { key: "completed", label: "✅ Completed Features", icon: CheckCircle2, color: "text-emerald-300 border-emerald-500/40 bg-emerald-500/5" },
  { key: "pending_credentials", label: "🔑 Pending Credentials", icon: KeyRound, color: "text-amber-300 border-amber-500/40 bg-amber-500/5" },
  { key: "upcoming_features", label: "🚀 Upcoming (in-flight)", icon: Rocket, color: "text-blue-300 border-blue-500/40 bg-blue-500/5" },
  { key: "future_features", label: "🌟 Future Features", icon: Sparkles, color: "text-purple-300 border-purple-500/40 bg-purple-500/5" },
  { key: "disabled_features", label: "⛔ Disabled Features", icon: Ban, color: "text-white/40 border-white/10 bg-white/5" },
];

export default function RoadmapPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/roadmap`, { withCredentials: true });
      setData(data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#0F172A] text-white pb-24" data-testid="roadmap-page">
      <header className="border-b border-gold/20 bg-[#0a1120]/60 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <button onClick={() => navigate("/admin/dashboard")} className="flex items-center gap-2 text-gold/80 hover:text-gold text-sm" data-testid="btn-back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> Admin Dashboard
          </button>
          <div className="flex items-center gap-2">
            <Map className="w-5 h-5 text-gold" />
            <h1 className="text-lg sm:text-xl font-heading text-gold">Roadmap & Pending Integrations</h1>
          </div>
          <div className="w-16" />
        </div>
      </header>

      {loading && <p className="text-center text-white/60 py-10">Loading...</p>}

      {data && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          {/* Live status banner */}
          <div className="rounded-2xl border border-gold/30 bg-gradient-to-br from-gold/10 to-transparent p-5" data-testid="live-status-banner">
            <h2 className="text-lg font-heading text-gold mb-3">Live Status Snapshot</h2>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-white/60 mb-1">🟢 Connected Integrations ({data.live_status?.connected_integrations?.length || 0})</p>
                <ul className="space-y-0.5">
                  {(data.live_status?.connected_integrations || []).map((c) => (
                    <li key={c.key} className="text-emerald-300 text-[13px]">• {c.name}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-white/60 mb-1">🟡 Pending Integrations ({data.live_status?.pending_integrations?.length || 0})</p>
                <ul className="space-y-0.5">
                  {(data.live_status?.pending_integrations || []).map((c) => (
                    <li key={c.key} className="text-amber-300 text-[13px]">• {c.name}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {SECTION_META.map(({ key, label, icon: Icon, color }) => {
            const list = data.static_roadmap?.[key] || [];
            if (list.length === 0 && key === "disabled_features") return null;
            return (
              <section key={key} data-testid={`roadmap-section-${key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4 text-gold" />
                  <h2 className="font-heading text-gold text-base">{label} ({list.length})</h2>
                </div>
                <ul className="space-y-2">
                  {list.map((item, i) => (
                    <li key={i} className={`p-3 rounded-lg border ${color}`} data-testid={`roadmap-item-${key}-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-white text-sm">{item.name}</p>
                          {item.requires && (
                            <p className="text-[12px] text-white/60 mt-1">
                              Requires: <code className="text-amber-300">{JSON.stringify(item.requires)}</code>
                            </p>
                          )}
                          {item.unblocks && (
                            <ul className="text-[12px] text-white/60 mt-1 list-disc list-inside">
                              {item.unblocks.map((u, j) => <li key={j}>{u}</li>)}
                            </ul>
                          )}
                          {item.docs && (
                            <a href={item.docs} target="_blank" rel="noopener noreferrer"
                               className="inline-flex items-center gap-1 text-[11px] text-gold hover:underline mt-1">
                              Setup Docs <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        {(item.version || item.eta || item.shipped) && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 text-white/70 border border-white/10 shrink-0">
                            {item.shipped ? `Shipped ${item.shipped}` : item.eta || item.version}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
